import { yupResolver } from "@hookform/resolvers/yup";
import {
  Button,
  CircularProgress,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import FeatherIcon from "feather-icons-react";
import React, { FC, useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { object, string } from "yup";
import { ProcurementSupplierDto } from "@green0meter/api-client";
import { useTranslation } from "react-i18next";
import { t } from "i18next";

import { StyledForm } from "./SupplierEditor.styled";

import Drawer from "@components/common/Drawer";
import {
  useCreateProcurementSupplier,
  useGetProcurementSuppliers,
  useUpdateProcurementSupplier,
} from "@api";
import { TextField, Grid } from "@components/base";
import { useUserStore } from "@store/user";
import DatePickerField from "@components/common/DatePickerField";
import { NaceDivision, NaceSection } from "@components/common/Nace";

const schema = object({
  name: string().max(255).required().trim(),
  email: string()
    .email("Must be a valid email")
    .max(255)
    .required(t("validation.required"))
    .trim(),
  vat: string().max(255).required(t("validation.required")).trim(),
}).required();

type FormValues = {
  name: string;
  email: string;
  vat: string;
  deadline?: string | null;
  naceSectionId?: string | null;
  naceDivisionId?: string | null;
  annualProcurementValue?: number | null;
};

interface SupplierEditor {
  open: boolean;
  onClose: () => void;
  procurementSupplier?: ProcurementSupplierDto;
}

const defaultValues: FormValues = {
  name: "",
  email: "",
  vat: "",
  deadline: null,
  naceSectionId: null,
  naceDivisionId: null,
  annualProcurementValue: null,
};

const SupplierEditor: FC<SupplierEditor> = ({
  open,
  onClose,
  procurementSupplier,
}) => {
  const { t } = useTranslation();
  const { user } = useUserStore();
  const { createProcurementSupplierAsync, createProcurementSupplierEnabled } =
    useCreateProcurementSupplier(user);
  const { updateProcurementSupplierAsync, updateProcurementSupplierEnabled } =
    useUpdateProcurementSupplier(user);

  const [isSaving, setIsSaving] = useState(false);
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
    reset,
    clearErrors,
    control,
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
  });
  const { procurementSuppliers } = useGetProcurementSuppliers(user);
  const naceSection = useWatch({ control, name: "naceSectionId" });
  const editMode = !!procurementSupplier;
  const suppliersCompanyIsRegistered = procurementSupplier?.isSellerRegistered;

  const onSubmit = async (data: FormValues): Promise<void> => {
    setIsSaving(true);

    if (procurementSupplier) {
      await updateProcurementSupplierAsync({
        procurementSupplierId: procurementSupplier.id,
        params: data,
      });
    } else {
      if (!procurementSuppliers?.items) {
        setIsSaving(false);
        await createProcurementSupplierAsync(data);

        return;
      } else {
        const existingSupplierByVat = procurementSuppliers?.items.find(
          (Supplier) => Supplier.vat.toLowerCase() === data.vat.toLowerCase(),
        );

        let hasError = false;

        if (existingSupplierByVat) {
          setError("vat", {
            type: "manual",
            message: t("supplierManagement.addSupplier.vatAlreadyRegistered"),
          });
          hasError = true;
        }

        if (hasError) {
          setIsSaving(false);

          return;
        }

        await createProcurementSupplierAsync(data);
      }
    }

    setIsSaving(false);
    reset(defaultValues);
    onClose();
  };

  useEffect(() => {
    if (!open) return;

    if (procurementSupplier) {
      reset({
        name: procurementSupplier.name ?? "",
        email: procurementSupplier.email ?? "",
        vat: procurementSupplier.vat ?? "",
        deadline: procurementSupplier.deadline ?? null,
        naceDivisionId: procurementSupplier.naceDivisionId ?? null,
        naceSectionId: procurementSupplier.naceSectionId ?? null,
        annualProcurementValue: procurementSupplier.annualProcurementValue ?? null,
      });
    } else {
      reset(defaultValues);
    }

    clearErrors();
  }, [open, procurementSupplier, reset, clearErrors]);

  return (
    ((editMode && updateProcurementSupplierEnabled) ||
      (!editMode && createProcurementSupplierEnabled)) && (
      <Drawer
        open={open}
        key={procurementSupplier?.id ?? "new"}
        onClose={() => {
          reset(defaultValues);
          onClose();
        }}
        content={
          <StyledForm onSubmit={handleSubmit(onSubmit)}>
            <Stack justifyContent="space-between" height="100%">
              <Stack spacing={4}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="flex-start"
                  spacing={1}
                >
                  <Stack spacing={1}>
                    <FeatherIcon icon="plus" size={24} />
                    <Typography>
                      {editMode
                        ? t("sellerEditor.editTitle")
                        : t("sellerEditor.addTitle")}
                    </Typography>
                  </Stack>
                  <IconButton onClick={onClose}>
                    <FeatherIcon icon="x" size={24} />
                  </IconButton>
                </Stack>
                <Stack spacing={2}>
                  <Stack spacing={1}>
                    <Typography>{t("sellerEditor.entityName")}</Typography>
                    <TextField
                      placeholder={t("sellerEditor.entityName")}
                      variant="filled"
                      slotProps={{
                        input: { ...register("name") },
                      }}
                      fullWidth
                      disabled={isSaving}
                    />
                  </Stack>
                  <Stack spacing={1}>
                    <Typography>{t("sellerEditor.email")}</Typography>
                    <TextField
                      placeholder="Email"
                      variant="filled"
                      slotProps={{
                        input: { ...register("email") },
                      }}
                      fullWidth
                      error={!!errors.email}
                      helperText={errors.email?.message}
                      disabled={isSaving}
                    />
                  </Stack>
                  <Stack spacing={1}>
                    <Typography>{t("sellerEditor.vat")}</Typography>
                    <TextField
                      placeholder="VAT"
                      variant="filled"
                      slotProps={{
                        input: { ...register("vat") },
                      }}
                      fullWidth
                      error={!!errors.vat}
                      helperText={errors.vat?.message}
                      disabled={
                        isSaving ||
                        !!procurementSupplier?.isRegistrationLinkSent ||
                        suppliersCompanyIsRegistered
                      }
                    />
                  </Stack>
                  <Stack spacing={1}>
                    <Typography>{t("sellerEditor.deadline")}</Typography>
                    <DatePickerField<FormValues>
                      label={t("sellerEditor.deadline")}
                      control={control}
                      name="deadline"
                    />
                  </Stack>
                  <Stack spacing={1}>
                    <Typography>{t("sellerEditor.nace")}</Typography>
                    <NaceSection<FormValues> control={control} />
                    {naceSection && (
                      <Grid size={{ xs: 12 }}>
                        <NaceDivision<FormValues>
                          naceSectionId={naceSection}
                          control={control}
                          isDisabled={isSaving}
                        />
                      </Grid>
                    )}
                  </Stack>
                  <Stack spacing={1}>
                    <Typography>
                      {t("sellerEditor.annualProcurementValue")}
                    </Typography>
                    <TextField
                      type="number"
                      placeholder={t("sellerEditor.annualProcurementValue")}
                      variant="filled"
                      slotProps={{
                        input: {
                          ...register("annualProcurementValue"),
                          inputMode: "decimal",
                          inputProps: { inputMode: "decimal", step: 0.01 },
                        },
                      }}
                      fullWidth
                      error={!!errors.annualProcurementValue}
                      helperText={errors.annualProcurementValue?.message}
                      disabled={isSaving}
                    />
                  </Stack>
                </Stack>
              </Stack>
              <Stack spacing={2} direction="row" justifyContent="flex-end">
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={onClose}
                  disabled={isSaving}
                >
                  {t("common.cancel")}
                </Button>
                <Button type="submit" variant="contained" disabled={isSaving}>
                  {isSaving ? (
                    <CircularProgress />
                  ) : editMode ? (
                    t("common.save")
                  ) : (
                    t("common.add")
                  )}
                </Button>
              </Stack>
            </Stack>
          </StyledForm>
        }
      />
    )
  );
};

export default SupplierEditor;
